/**
 * Validates a returnUrl to prevent open redirect vulnerabilities
 *
 * @param returnUrl - The URL to validate
 * @returns true if the URL is a valid internal path, false otherwise
 *
 * Rules:
 * - Must be a relative path (starts with /)
 * - Must not be protocol-relative (starts with //)
 * - Must not contain :// (absolute URL)
 * - Must not be empty
 * - Must not contain null bytes or other dangerous characters
 */
export const validateReturnUrl = (
  returnUrl: string | null | undefined
): boolean => {
  if (!returnUrl || typeof returnUrl !== "string") {
    return false;
  }

  // Must be a relative path (starts with /)
  if (!returnUrl.startsWith("/")) {
    return false;
  }

  // Must not be protocol-relative (starts with //)
  if (returnUrl.startsWith("//")) {
    return false;
  }

  // Must not contain :// (absolute URL)
  if (returnUrl.includes("://")) {
    return false;
  }

  // Must not contain null bytes or other dangerous characters
  if (
    returnUrl.includes("\0") ||
    returnUrl.includes("\r") ||
    returnUrl.includes("\n")
  ) {
    return false;
  }

  // Must not be just "/" (we'll use "/" as fallback, but it's valid)
  // Allow "/" as it's a valid internal path

  return true;
};

/**
 * Gets a safe returnUrl, falling back to "/" if invalid
 *
 * @param returnUrl - The URL to validate and return
 * @returns A valid returnUrl or "/" as fallback
 */
export const getSafeReturnUrl = (
  returnUrl: string | null | undefined
): string => {
  if (validateReturnUrl(returnUrl)) {
    return returnUrl as string;
  }
  return "/";
};
